import { useCallback, useEffect, useMemo, useState } from 'react';
import { css, cx } from '@linaria/atomic';
import { GlsOrg, WORKSPACE_TAG } from '@wetags/gls';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  LocationGroup,
  LocationGroupSection,
  LocationPickerHeader,
  useLocationPicker,
  LocationSearchGroup,
  LocationSearchResults,
  GroupSearchResults,
  OrgSearchResults,
  useLocationPickerStore,
  LocationGroupSectionSimpleView,
} from '@frontend/location-picker';
import { useLocationGroupStore, useSelectedScope } from '@frontend/scope';
import { pendo } from '@frontend/tracking';
import { useWorkspaceShallowStore, useWorkspaceState } from '@frontend/workspace-scope';
import { GlsWorkspaceSection } from '@frontend/workspaces';
import { theme } from '@frontend/theme';
import { Button, Text, emptyStateGraphics, Truncated } from '@frontend/design-system';
import { UnprivilegedPickerProvider, useUnprivilegedPicker } from './unprivileged-picker-context';

export interface LocationGroupResult {
  groupId: string;
  groupName: string;
  orgId: string;
  orgName: string;
  locations: Array<{
    id: string;
    name: string;
  }>;
}

interface UnprivilegedLocationPickerProps {
  scopeHierarchy: Record<string, Record<string, string[]>>;
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  selectedLocationIds: string[];
  selectedGroupId: string;
  selectedOrgId: string;
  selectedScope?: {
    scopeType?: string;
    scopeId?: string;
  };
  closeModal?: () => void;
  children: React.ReactNode;
  renderOrgInfo?: (orgId: string) => JSX.Element | null;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  renderBannerInfo?: () => JSX.Element | null;
  onApplyError?: (error: unknown) => void;
  onApply: (selectedOrgId: string, selectedGroupId: string, selectedLocationIds: string[]) => Promise<void>;
  onClickOrgRadio?: () => void;
  isSimpleView?: boolean;
}

const hasPendingOrgSwitch = ({
  selectedOrgId,
  organizationViewId,
  inFlightOrganizationId,
}: {
  selectedOrgId: string;
  organizationViewId: string;
  inFlightOrganizationId: string;
}) => {
  if (!selectedOrgId) {
    return false;
  }

  const effectiveOrganizationViewId = organizationViewId || selectedOrgId;
  const effectiveInFlightOrganizationId = inFlightOrganizationId || effectiveOrganizationViewId;

  return effectiveOrganizationViewId !== selectedOrgId || effectiveInFlightOrganizationId !== selectedOrgId;
};

const getSelectedGroupIdsForOrg = ({
  orgScopeHierarchy,
  selectedLocationIds,
}: {
  orgScopeHierarchy: Record<string, string[]>;
  selectedLocationIds: string[];
}) => {
  if (!orgScopeHierarchy || selectedLocationIds.length === 0) {
    return [];
  }

  const selectedLocationSet = new Set(selectedLocationIds);

  return Object.entries(orgScopeHierarchy)
    .filter(([, locationIds]) => locationIds.some((locationId) => selectedLocationSet.has(locationId)))
    .map(([groupId]) => groupId);
};

export const UnprivilegedLocationProvider = ({
  getScopeName,
  scopeHierarchy,
  selectedLocationIds,
  selectedGroupId,
  selectedOrgId,
  selectedScope,
  closeModal,
  children,
  renderBannerInfo,
  renderLocationInfo,
  renderOrgInfo,
  onApplyError,
  onApply,
  onClickOrgRadio,
  isSimpleView,
}: UnprivilegedLocationPickerProps) => {
  const locationPickerStore = useLocationPickerStore();
  useEffect(() => {
    if (!onClickOrgRadio) return;

    const unsubscribe = locationPickerStore.subscribe(
      (state) => state.inFlightOrganizationId,
      (_) => {
        onClickOrgRadio();
      }
    );

    return () => {
      unsubscribe();
    };
  }, [onClickOrgRadio]);

  return (
    <UnprivilegedPickerProvider
      scopeHierarchy={scopeHierarchy}
      getScopeName={getScopeName}
      selectedOrgId={selectedOrgId}
      selectedGroupId={selectedGroupId}
      selectedLocationIds={selectedLocationIds}
      selectedScope={selectedScope}
      closeModal={closeModal}
      renderBannerInfo={renderBannerInfo}
      renderLocationInfo={renderLocationInfo}
      renderOrgInfo={renderOrgInfo}
      onApplyError={onApplyError}
      onApply={onApply}
      isSimpleView={isSimpleView}
    >
      {children}
    </UnprivilegedPickerProvider>
  );
};

const LocationPickerFooter = () => {
  const locationPickerStore = useLocationPickerStore();
  const { inFlightOrganizationId, inFlightLocationIds, organizationViewId } = useLocationPicker([
    'inFlightOrganizationId',
    'inFlightLocationIds',
    'organizationViewId',
  ]);
  const { pendingWorkspaceId, commitPendingWorkspace } = useWorkspaceShallowStore(
    'pendingWorkspaceId',
    'commitPendingWorkspace'
  );
  const [filterLoading, setFilterLoading] = useState(false);
  const closeModal = useUnprivilegedPicker((state) => state.closeModal);
  const onApplyError = useUnprivilegedPicker((state) => state.onApplyError);
  const onApply = useUnprivilegedPicker((state) => state.onApply);
  const hasSwitchedScope = useUnprivilegedPicker((state) => state.hasSwitchedScope);
  const hasAccessToGroupOnly = useUnprivilegedPicker((state) => state.hasAccessToGroupOnly);
  const hasInFlightEmptyGroup = useUnprivilegedPicker((state) => state.hasInFlightEmptyGroup);
  const isSimpleView = useUnprivilegedPicker((state) => state.isSimpleView);
  const scopeHierarchy = useUnprivilegedPicker((state) => state.scopeHierarchy);
  const totalNumOfLocations = useLocationGroupStore.getState().accessibleLocationIds.length;
  const { t } = useTranslation('location-picker');

  const selectedGroupIds = useMemo(
    () =>
      getSelectedGroupIdsForOrg({
        orgScopeHierarchy: scopeHierarchy[inFlightOrganizationId] || {},
        selectedLocationIds: inFlightLocationIds,
      }),
    [scopeHierarchy, inFlightOrganizationId, inFlightLocationIds]
  );
  const selectedGroupIdForApply = useMemo(
    () => (selectedGroupIds.length === 1 ? selectedGroupIds[0] : ''),
    [selectedGroupIds]
  );

  const isApplyDisabled = () => {
    const groupOnly = hasAccessToGroupOnly();
    const isWorkspacePendingSelection = pendingWorkspaceId !== null;
    const hasManualLocations = inFlightLocationIds.length > 0;
    const hasSingleParentManualSelection = selectedGroupIds.length === 1;

    if (groupOnly === 'already-selected-empty-group') return true;

    if (groupOnly === 'available-empty-group' || hasInFlightEmptyGroup) return false;

    if (!hasSwitchedScope || !inFlightOrganizationId) return true;

    if (!isWorkspacePendingSelection) {
      return !hasManualLocations || !hasSingleParentManualSelection;
    }

    return !hasManualLocations;
  };

  const handleApplySelection = async () => {
    try {
      setFilterLoading(true);
      const { inFlightOrganizationId, inFlightGroupId, inFlightLocationIds } = locationPickerStore.getState();
      pendo.track('location_selector_apply_click', {
        selectedLocationCount: inFlightLocationIds.length,
        totalLocationsAvailable: totalNumOfLocations,
      });
      const getLocationIds = () => {
        if (hasAccessToGroupOnly() === 'available-empty-group') {
          return Object.keys(scopeHierarchy[organizationViewId] || {});
        }

        if (hasInFlightEmptyGroup) {
          return [inFlightGroupId];
        }

        return inFlightLocationIds;
      };

      const organizationId =
        hasAccessToGroupOnly() === 'available-empty-group' ? organizationViewId : inFlightOrganizationId;
      const selectedGroupId = pendingWorkspaceId !== null ? inFlightGroupId : selectedGroupIdForApply;

      await onApply(organizationId, selectedGroupId, getLocationIds());

      commitPendingWorkspace();

      closeModal?.();
    } catch (error) {
      onApplyError ? onApplyError(error) : console.error(error);
    } finally {
      setFilterLoading(false);
    }
  };

  return (
    <div className={footerStyle}>
      <InFlightSelection />
      <div className={footerActionsStyle}>
        {!isSimpleView && (
          <Button variant='secondary' size='large' onClick={closeModal}>
            {t('Cancel')}
          </Button>
        )}
        <Button disabled={isApplyDisabled()} loading={filterLoading} size='large' onClick={handleApplySelection}>
          {t('Apply')}
        </Button>
      </div>
    </div>
  );
};

export const UnprivilegedLocationPicker = () => {
  const isSimpleView = useUnprivilegedPicker((state) => state.isSimpleView);

  return (
    <>
      {!isSimpleView && <CurrentlyAppliedScopeWrapper />}
      <LocationPickerHeaderWrapper />
      <LocationPickerBody />
      <LocationPickerBanners />
      <LocationPickerFooter />
    </>
  );
};

export const LocationPickerBanners = () => {
  const renderBannerInfo = useUnprivilegedPicker((state) => state.renderBannerInfo);

  return renderBannerInfo?.();
};

const CurrentlyAppliedScopeWrapper = () => {
  const selectedLocationIds = useUnprivilegedPicker((state) => state.selectedLocationIds);
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const selectedOrgId = useUnprivilegedPicker((state) => state.selectedOrgId);
  const selectedGroupId = useUnprivilegedPicker((state) => state.selectedGroupId);
  const locationNames = useUnprivilegedPicker((state) => state.getLocationNames(selectedLocationIds));

  return (
    <CurrentlyAppliedScope
      organizationName={getScopeName(selectedOrgId, 'org')}
      groupName={getScopeName(selectedGroupId, 'group')}
      locationName={locationNames}
    />
  );
};

const LocationPickerHeaderWrapper = () => {
  const searchResultCount = useUnprivilegedPicker((state) => state.searchResultCount);
  const orgIds = useUnprivilegedPicker((state) => state.orgIds);
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const renderOrgInfo = useUnprivilegedPicker((state) => state.renderOrgInfo);

  return (
    <LocationPickerHeader
      count={searchResultCount}
      organizationIds={orgIds}
      getScopeName={getScopeName}
      renderOrgInfo={renderOrgInfo}
    />
  );
};

const footerStyle = css`
  padding: ${theme.spacing(2)};
  gap: ${theme.spacing(2)};
  border-top: 1px solid ${theme.colors.neutral20};
`;

const footerActionsStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing(2)};
`;

const containerBaseStyle = css`
  overflow: hidden;
`;

const textAlignLeftStyle = css`
  text-align: left;
`;

const textAlignRightStyle = css`
  text-align: right;
`;

const containerStyle = (alignText: 'left' | 'right' = 'left') =>
  cx(containerBaseStyle, alignText === 'right' ? textAlignRightStyle : textAlignLeftStyle);

const breadcrumbsContainerBaseStyle = css`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${theme.spacing(1)};
  max-width: 400px;
  margin-bottom: ${theme.spacing(3)};
`;

const breadcrumbsRightStyle = css`
  justify-content: flex-end;
  margin-left: auto;
`;

const breadcrumbsLeftStyle = css`
  justify-content: flex-start;
`;

const breadcrumbsContainerStyle = (alignText: 'left' | 'right' = 'left') =>
  cx(breadcrumbsContainerBaseStyle, alignText === 'right' ? breadcrumbsRightStyle : breadcrumbsLeftStyle);

const currentlyAppliedScopeStyle = css`
  margin-left: ${theme.spacing(3)};
`;

const emptyStateGraphicStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${theme.spacing(3, 5)};
`;

const emptyStateImageStyle = css`
  max-width: 300px;
  max-height: 300px;
`;

const emptyStateTextStyle = css`
  width: 65%;
`;

const locationGroupPaddingStyle = css`
  padding: ${theme.spacing(1, 2)};
`;

export const InFlightSelection = () => {
  const { t } = useTranslation('location-picker');
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const hasAccessToGroupOnly = useUnprivilegedPicker((state) => state.hasAccessToGroupOnly);

  const { inFlightOrganizationId, inFlightGroupId, inFlightLocationIds, organizationViewId } = useLocationPicker([
    'inFlightOrganizationId',
    'inFlightGroupId',
    'inFlightLocationIds',
    'organizationViewId',
  ]);

  const hasSwitchedScope = useUnprivilegedPicker((state) => state.hasSwitchedScope);
  const scopeHierarchy = useUnprivilegedPicker((state) => state.scopeHierarchy);

  const locationNames = useUnprivilegedPicker((state) => state.getLocationNames(inFlightLocationIds));

  const hasInFlightEmptyGroup = useUnprivilegedPicker((state) => state.hasInFlightEmptyGroup);

  const shouldDisplayScopeChangeIndicator = () => {
    /*This check is to ensure that we don't show the "Switching to" section
     when the user is already in the group-only access view or if they selected a group
     that has no locations.
    */
    if (hasAccessToGroupOnly() === 'already-selected-empty-group') {
      return false;
    }

    if (hasAccessToGroupOnly() === 'available-empty-group' || hasInFlightEmptyGroup) {
      return true;
    }

    return hasSwitchedScope;
  };

  const displayScopeChangeIndicatorForGroupOnly = () => {
    return hasInFlightEmptyGroup || hasAccessToGroupOnly() === 'available-empty-group';
  };

  const displayScopeChangeIndicatorForGroupOnlyText = () => {
    if (hasInFlightEmptyGroup) {
      return `${getScopeName(inFlightGroupId, 'group')}`;
    }

    const orgOnlyGroup = Object.keys(scopeHierarchy[organizationViewId])[0];
    const orgOnlyGroupName = getScopeName(orgOnlyGroup, 'group');

    return orgOnlyGroupName;
  };

  return (
    <AnimatePresence>
      {shouldDisplayScopeChangeIndicator() ? (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          exit={{ height: 0 }}
          key={`${displayScopeChangeIndicatorForGroupOnly()}`}
          className={containerStyle('right')}
        >
          {displayScopeChangeIndicatorForGroupOnly() ? (
            <div className={breadcrumbsContainerStyle('right')}>
              <Text size='large' weight='light' color='subdued'>
                <Text weight='light' color='subdued'>
                  {t('Switching to: ')}
                </Text>
                <Text as='span' weight='bold' color='default'>
                  {displayScopeChangeIndicatorForGroupOnlyText()}
                </Text>
                <Text color='subdued' size='small'>
                  {t(`This location group contains locations that you are not authorized to access. To manage your
                  organization's settings, click 'Apply'.`)}
                </Text>
              </Text>
            </div>
          ) : (
            <LocationBreadcrumbDisplay
              label={t('Switching to:')}
              organizationName={getScopeName(inFlightOrganizationId, 'org')}
              groupName={getScopeName(inFlightGroupId)}
              locationName={locationNames}
              alignText='right'
            />
          )}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const LocationBreadcrumbDisplay = ({
  label,
  organizationName,
  groupName,
  locationName,
  alignText = 'left',
}: {
  label: string;
  organizationName: string;
  groupName: string;
  locationName: string;
  alignText?: 'left' | 'right';
}) => {
  const { hasSingleOrganization, hasSingleLocationGroup } = useLocationPicker([
    'hasSingleOrganization',
    'hasSingleLocationGroup',
  ]);

  return (
    <>
      <Text size='large' color='subdued'>
        {label}
      </Text>
      <div className={breadcrumbsContainerStyle(alignText)}>
        {!hasSingleOrganization && (
          <>
            <LocationBreadcrumb>{organizationName || '...'}</LocationBreadcrumb>
            <Icon name='caret-right-small' />
          </>
        )}
        {!hasSingleLocationGroup && (
          <>
            <LocationBreadcrumb>{groupName || '...'}</LocationBreadcrumb>
            <Icon name='caret-right-small' />
          </>
        )}
        <LocationBreadcrumb>{locationName || '...'}</LocationBreadcrumb>
      </div>
    </>
  );
};

const CurrentlyAppliedScope = ({
  organizationName,
  groupName,
  locationName,
}: {
  organizationName: string;
  groupName: string;
  locationName: string;
}) => {
  const { t } = useTranslation('location-picker');

  if (!organizationName && !groupName && !locationName) {
    return null;
  }

  return (
    <div className={cx(containerStyle(), currentlyAppliedScopeStyle)}>
      <LocationBreadcrumbDisplay
        label={t('Currently Applied:')}
        organizationName={organizationName}
        groupName={groupName}
        locationName={locationName}
      />
    </div>
  );
};

InFlightSelection.displayName = 'InFlightSelection';

const breadcrumbStyle = css`
  color: ${theme.font.colors.default};
  flex: 0 1 auto !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline;
  text-align: start;
  width: auto;
`;

const LocationBreadcrumb = ({ children }: { children: React.ReactNode }) => (
  <Truncated as={Text} size='large' className={breadcrumbStyle}>
    {children}
  </Truncated>
);

const EmptyState = emptyStateGraphics.search;

const EmptyStateGraphic = () => {
  return (
    <div className={emptyStateGraphicStyle}>
      <EmptyState className={emptyStateImageStyle} />
      <Text className={emptyStateTextStyle} textAlign='center' color='light' size='large'>
        Start searching Organizations, Location Groups, or Locations using the search bar above.
      </Text>
    </div>
  );
};

// Stable empty array to avoid creating a new reference on every render
// when isSwitchingOrg || isSearchMode is true.
const EMPTY_LOCATION_IDS: string[] = [];

const WorkspaceSection = () => {
  const { isSearchMode, inFlightOrganizationId, inFlightLocationIds, organizationViewId } = useLocationPicker([
    'isSearchMode',
    'inFlightOrganizationId',
    'inFlightLocationIds',
    'organizationViewId',
  ]);
  const { setInFlightLocationIds } = useLocationPicker(['setInFlightLocationIds']);
  const selectedOrgId = useUnprivilegedPicker((state) => state.selectedOrgId);
  const scopeHierarchy = useUnprivilegedPicker((state) => state.scopeHierarchy);
  const isSimpleView = useUnprivilegedPicker((state) => state.isSimpleView);
  const { pendingWorkspaceId, setPendingWorkspaceId } = useWorkspaceShallowStore(
    'pendingWorkspaceId',
    'setPendingWorkspaceId'
  );
  const isSwitchingOrg = hasPendingOrgSwitch({
    selectedOrgId,
    organizationViewId,
    inFlightOrganizationId,
  });
  const workspaceOrgId = organizationViewId || inFlightOrganizationId || selectedOrgId;

  const allLocationIds = useMemo(
    () =>
      isSwitchingOrg || isSearchMode
        ? EMPTY_LOCATION_IDS
        : Array.from(new Set(Object.values(scopeHierarchy[workspaceOrgId] || {}).flat())),
    [isSearchMode, isSwitchingOrg, scopeHierarchy, workspaceOrgId]
  );
  const { shouldShowWorkspaces } = useWorkspaceState(workspaceOrgId);
  const showWorkspaces = !isSwitchingOrg && !isSearchMode && shouldShowWorkspaces && !isSimpleView;

  // Stable callback reference — prevents GlsWorkspaceSection from re-rendering
  // due to a new function reference on every parent render.
  const handleSelectWorkspace = useCallback(
    (locationIds: string[]) => setInFlightLocationIds(locationIds),
    [setInFlightLocationIds]
  );

  useEffect(() => {
    if (isSwitchingOrg && pendingWorkspaceId !== null) {
      setPendingWorkspaceId(null);
    }
  }, [isSwitchingOrg, pendingWorkspaceId]);

  if (!showWorkspaces) {
    return null;
  }

  return (
    <GlsWorkspaceSection
      orgId={workspaceOrgId}
      accessibleLocationIds={allLocationIds}
      selectedLocationIds={inFlightLocationIds}
      onSelectWorkspace={handleSelectWorkspace}
    />
  );
};

/**
 * Lightweight gate that prevents WorkspaceSection from mounting
 * (and running when the workspace feature is disabled) until the wetag has been loaded.
 */
const WorkspaceSectionGate = () => {
  const [isEnabled, isLoading] = GlsOrg.useWetag(WORKSPACE_TAG);
  if (isLoading || !isEnabled) return null;
  return <WorkspaceSection />;
};

const LocationPickerBody = () => {
  const { isSearchMode } = useLocationPicker(['isSearchMode']);

  return (
    <div style={{ overflow: 'auto', flex: 1 }}>
      <WorkspaceSectionGate />
      {!isSearchMode ? <LocationGroupsDisplay /> : <SearchResultsDisplay />}
    </div>
  );
};

const SearchResultsDisplay = () => {
  const { organizationViewId, inFlightOrganizationId } = useLocationPicker([
    'organizationViewId',
    'inFlightOrganizationId',
  ]);
  const selectedOrgId = useUnprivilegedPicker((state) => state.selectedOrgId);
  const isSwitchingOrg = hasPendingOrgSwitch({
    selectedOrgId,
    organizationViewId,
    inFlightOrganizationId,
  });
  const { setInFlightLocationIds } = useLocationPicker(['setInFlightLocationIds']);
  const searchResults = useUnprivilegedPicker((state) => state.searchResults);
  const orgLocationGroups = useUnprivilegedPicker((state) => state.orgLocationGroups);
  const scopeHierarchy = useUnprivilegedPicker((state) => state.scopeHierarchy);
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const hasSearchResults = useUnprivilegedPicker((state) => state.hasSearchResults);
  const renderLocationInfo = useUnprivilegedPicker((state) => state.renderLocationInfo);
  const selectedScope = useSelectedScope();
  const { pendingWorkspaceId } = useWorkspaceShallowStore('pendingWorkspaceId');
  // Allow cross-group selection when in workspace mode OR when a workspace is pending selection
  const allowCrossGroupSelection =
    !isSwitchingOrg && (selectedScope?.scopeType === 'workspace' || pendingWorkspaceId !== null);

  if (!hasSearchResults) {
    return <EmptyStateGraphic />;
  }

  return (
    <>
      {searchResults.locationGroups.length > 0 && (
        <LocationSearchGroup id='locations'>
          {searchResults.locationGroups.map((group) => (
            <LocationSearchResults
              renderLocationInfo={renderLocationInfo}
              key={group.groupId}
              groupResults={group}
              allowCrossGroupSelection={allowCrossGroupSelection}
            />
          ))}
        </LocationSearchGroup>
      )}

      {searchResults.groups.length > 0 && (
        <LocationSearchGroup id='locationGroups'>
          {searchResults.groups.map((group) => (
            <GroupSearchResults
              key={group.id}
              organizationName={group.orgName}
              organizationId={group.orgId}
              locationGroupId={group.id}
              locationGroupName={group.name}
              numberOfLocations={group.locationCount || 0}
              locations={scopeHierarchy[group.orgId]?.[group.id] || []}
              getScopeName={getScopeName}
              allowCrossGroupSelection={allowCrossGroupSelection}
              onSelected={() => {
                setInFlightLocationIds(scopeHierarchy[group.orgId][group.id]);
              }}
            />
          ))}
        </LocationSearchGroup>
      )}

      {searchResults.organizations.length > 0 && (
        <LocationSearchGroup id='organizations'>
          {searchResults.organizations.map((org) => (
            <OrgSearchResults
              key={org.id}
              organizationName={org.name}
              organizationId={org.id}
              numberOfGroups={org.groupCount || 0}
              numberOfLocations={org.locationCount || 0}
              locationGroups={orgLocationGroups[org.id] || []}
              getScopeName={getScopeName}
              allowCrossGroupSelection={allowCrossGroupSelection}
            />
          ))}
        </LocationSearchGroup>
      )}
    </>
  );
};

const LocationGroupsDisplay = () => {
  const { organizationViewId, inFlightOrganizationId } = useLocationPicker([
    'organizationViewId',
    'inFlightOrganizationId',
  ]);
  const selectedOrgId = useUnprivilegedPicker((state) => state.selectedOrgId);
  const isSwitchingOrg = hasPendingOrgSwitch({
    selectedOrgId,
    organizationViewId,
    inFlightOrganizationId,
  });

  const currentOrgLocationGroups = useUnprivilegedPicker((state) => state.currentOrgLocationGroups);
  const currentOrgData = useUnprivilegedPicker((state) => state.currentOrgData);
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const renderLocationInfo = useUnprivilegedPicker((state) => state.renderLocationInfo);
  const selectedScope = useSelectedScope();
  const { pendingWorkspaceId } = useWorkspaceShallowStore('pendingWorkspaceId');
  // Allow cross-group selection when in workspace mode OR when a workspace is pending selection
  const allowCrossGroupSelection =
    !isSwitchingOrg && (selectedScope?.scopeType === 'workspace' || pendingWorkspaceId !== null);

  return currentOrgLocationGroups.length > 1 ? (
    <LocationGroup key={organizationViewId}>
      {currentOrgLocationGroups.map((locationGroup) => (
        <LocationGroupSection
          key={locationGroup}
          groupId={locationGroup}
          groupLocations={currentOrgData[locationGroup] || []}
          getScopeName={getScopeName}
          renderLocationInfo={renderLocationInfo}
          allowCrossGroupSelection={allowCrossGroupSelection}
        >
          {getScopeName(locationGroup, 'group')}
        </LocationGroupSection>
      ))}
    </LocationGroup>
  ) : (
    <div className={locationGroupPaddingStyle}>
      <LocationGroupSectionSimpleView
        groupId={currentOrgLocationGroups[0]}
        groupLocations={currentOrgData[currentOrgLocationGroups[0]] || []}
        getScopeName={getScopeName}
        renderLocationInfo={renderLocationInfo}
        allowCrossGroupSelection={allowCrossGroupSelection}
      />
    </div>
  );
};
